import { DynamicComponent, Layout } from '@components/common'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import Storyblok, { StoryblokContent, useStoryblok } from '@lib/storyblok'
import { getAllProducts } from '@lib/productCatalog/getAllProducts'
import { SbEditableContent } from 'storyblok-react'
import { ProductStory } from 'types/storyblok'
import { trackViewItem } from '@lib/datalayer'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { read } from 'fs/promises'
import { fetchPosts } from '@lib/blog'
import commerce from '@lib/api/commerce'
import { Product } from '@commerce/types/product'
import { EnrichedProduct } from '@lib/productCatalog'

export async function fetchPaths() {
  const requestParams = {
    starts_with: 'landers/',
    per_page: null,
    page: null,
  }
  const result = await Storyblok.get('cdn/stories', requestParams)

  return result
}

export async function getStaticProps({
  params,
  preview = false,
}: GetStaticPropsContext<{
  slug: string
}>) {
  const version = 'draft'
  const sbParams = {
    version,
    resolve_relations:
      'landing-page.product,related-products.products,related-products.diagnostic_product',
  }

  const {
    data: { story },
  } = await Storyblok.get(`cdn/stories/landers/${params?.slug}`, sbParams)

  // Resolve the post relations in the read more component if it exists on the page.
  // This has to be done separately as we need to also load the author relations and Storyblok doesn't support nested relations.
  let readMore = null
  if (story.content.body) {
    readMore = story.content.body.find((blok: StoryblokContent) => {
      return blok.component === 'read-more'
    })
  }

  if (readMore) {
    const {
      data: { stories },
    } = await fetchPosts({
      showDrafts: version === 'draft',
      storyblokParams: {
        by_uuids: readMore.posts.join(','),
      },
    })

    readMore.posts = stories
  }

  const productStories = await getAllProducts()

  // Get the enriched product for the page if it is in the story content.
  if (story.content.product || story.content.cta_url) {
    let productData: EnrichedProduct | null = null
    if (story.content.product) {
      const product = productStories.find((productStory: ProductStory) => {
        return productStory.slug === story.content.product.slug
      })

      if (product !== undefined) {
        productData = product
      }
    }

    // Add the product data to each component.
    story.content.body.forEach((blok: StoryblokContent) => {
      blok.product_data = {
        product: productData,
        cta_url: story.content.cta_url || `/product/${productData?.slug}`,
      }
    })
  }

  // Load related products from commerce.
  let relatedProducts: any = null
  if (story.content.body) {
    relatedProducts = story.content.body.find((blok: StoryblokContent) => {
      return blok.component === 'related-products'
    })
  }

  if (relatedProducts) {
    const commerceProducts = []

    for (const relatedProduct of relatedProducts.products) {
      const product = productStories.find((story: ProductStory) => {
        return story.slug === relatedProduct.slug
      })
      if (product) {
        commerceProducts.push(product)
      }
    }
    relatedProducts.products = commerceProducts

    // Get the enriched diagnostic product for the related-products component if in the story content.
    if (relatedProducts.diagnostic_product) {
      const product = productStories.find((story: ProductStory) => {
        return story.slug === relatedProducts.diagnostic_product.slug
      })
      if (product) {
        relatedProducts.diagnostic_product = product
      }
    }
  }

  return {
    props: {
      story,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths() {
  const results = await fetchPaths()

  return {
    paths: results.data.stories.map((story: { slug: any }) => {
      return {
        params: {
          slug: story.slug,
        },
      }
    }),
    fallback: false,
  }
}

export default function Slug({
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const liveStory = useStoryblok(story)

  const components = liveStory.content.body.map((blok: SbEditableContent) => {
    return <DynamicComponent blok={blok} key={blok._uid} />
  })

  return <div>{components}</div>
}

Slug.Layout = Layout
